Devuelve los detalles públicos de una llamada específica de la organización autenticada.

## Qué entrega este endpoint

- clasificación operativa completa de la llamada;
- referencias públicas de campaña y agente;
- metadatos del número de destino;
- timestamps principales del ciclo de la llamada;
- grabación, variables, datos recolectados sanitizados, segmentos públicos y origen de terminación.

<ApiCodeExamples exampleId="callDetail" />

<ApiResponseExamples exampleId="callDetail" />

## Campos principales de la respuesta `200`

- `id`: ID de la llamada.
- `canonical_url`: enlace canónico de la llamada en la app Contact-Tel.
- `campaign_id` y `campaign_name`: campaña asociada, cuando exista.
- `campaign_contact_id` y `campaign_contact_display_name`: referencia pública del contacto relacionado.
- `public_api_call_contact_id` y `public_api_call_contact_status`: correlación del job padre público.
- `client_reference`: referencia opaca del cliente almacenada en el job padre, o `null` cuando no aplica.
- `retry_policy`: snapshot inmutable de la política del job padre, o `null` para llamadas del flujo anterior/no públicas.
- `attempts`: progreso de marcación del job padre con `count`, `remaining` y `next_attempt_at`.
- `public_api_call_contact_phone_id`, `public_api_call_contact_phone_status` y `public_api_call_contact_phone_position`: correlación del teléfono/intento dentro del job público.
- `created_by_id`, `created_by_name`, `created_by_extension`: referencia del usuario creador/originador.
- `transfer_target_user_*`, `transfer_target_group_*`, `interaction_claimed_by_*`, `interaction_answered_by_*`: referencias de transferencia humana y participantes.
- `telephony_transfer_*`: línea de tiempo del handoff humano.
- `sip_trunk_id`, `sip_trunk_name`: snapshot compatible de la troncal SIP.
- `sip_trunk_pool_id`, `sip_trunk_pool_name`, `sip_trunk_pool_member_id`: snapshot del pool/miembro de salida cuando aplique.
- `sip_trunk_outbound_route_id`, `sip_trunk_outbound_route_name`: ruta de salida usada por la llamada.
- `routing_decision`: snapshot público de la decisión de roteamiento de salida cuando exista.
- `ai_billed_seconds`, `ai_gross_billed_seconds`, `ai_refunded_seconds`: consumo de IA líquido, bruto y estornado.
- `telephony_billed_seconds`, `telephony_gross_billed_seconds`, `telephony_refunded_seconds`: consumo de telefonía líquido, bruto y estornado.
- `to_number_*`: metadatos públicos resueltos para el número de destino.
- `from_number_real`: origen real observado en el proveedor, cuando exista.
- `recording_download_url`: URL temporal de descarga de la grabación cuando exista.
- `variables`: variables resueltas de llamada/contacto usadas en la ejecución, excepto variables internas `sys__*`.
- `data_collected`: datos conversacionales consolidados desde los segmentos públicos. Cada item contiene solo `value`, `rationale` y `__source` sanitizado.
- `segments`: segmentos públicos con transcripción, resumen y grabación.
- `segments[].transcript_full`: transcripción estructurada sanitizada. Cada item contiene solo `role`, `message`, `original_message` y `time_in_call_secs`.
- `ring_duration`: duración del ringing en segundos.
- `ivr_audio_heard_seconds`, `ivr_audio_heard_to_end`: evidencia de reproducción Reverse IVR.
- `termination_origin`: origen público que causó el término de la llamada.

## Referencia de opciones

- `direction`: `outbound` significa llamada originada por la plataforma; `inbound` significa llamada recibida.
- `origin`: `campaign` fue creada por campaña; `manual` fue creada por usuario/operador; `public_api` fue creada por la API pública.
- `call_type`: `ai_agent` usa agente de IA; `reverse_ivr` usa audio y DTMF; `sip` es una llamada SIP.
- `status`: `pending`, `queued`, `dialing`, `analyzing`, `ringing`, `in_progress`, `finalizing`, `waiting_capacity`, `waiting_operating_hours`, `completed`, `no_interaction`, `failed`, `no_answer`, `no_answer_timeout`, `busy`, `cancelled`, `transfer_unanswered`, `congestion`, `voicemail`, `invalid_number`, `config_error`, `declined`, `unreachable`, `timeout`, `mismatch`, `api_timeout`, `terminated_manual`, `capacity_timeout`, `operating_window_timeout`.
- `to_number_phone_type`: `unknown`, `mobile`, `landline` o `sip`.
- `termination_origin`: `unknown`, `pstn`, `agent`, `manual`, `system` o `internal`.
- `segments[].segment_type`: `ai`, `telephony_internal_extension`, `telephony_outbound_internal_extension`, `telephony_external_extension`, `telephony_external_phone` o `ivr`.

## Cuándo usar este endpoint

- consultar una llamada específica a partir del `id` obtenido en la lista;
- habilitar drill-down operativo en sistemas externos;
- investigar el estado y el resultado de una llamada sin usar la interfaz administrativa.
